// Главная: что это одной фразой и развилка на два направления (макет: «Главная · развилка»
// и «Компьютер · главная»). Цены и миры — с сервера.
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import type { Catalog, Direction } from "../../api/types";
import { doorsFor, type Door } from "../../site/directionDoors";
import { DoorInside } from "../../ui/DirectionDoor";
import doorStyles from "../../ui/DirectionDoor.module.css";
import { useCatalog } from "../../app/queries";
import { SiteFooter } from "../../site/SiteFooter";
import { SiteHeader } from "../../site/SiteHeader";
import { SHOWCASE_PATH, directionClass } from "../../site/directionPaths";
import { priceOf } from "../../site/prices";
import { SIZES_HALF, SIZES_THIRD, picture, type PicturePriority } from "../../site/pictures";
import { Icon } from "../../ui/icons";
import { Message, Tag } from "../../ui/Message";
import { MAIN_SLOGAN, Slogan } from "../../ui/Slogan";
import styles from "./Home.module.css";

function DoorCard({ door, catalog, priority }: { door: Door; catalog: Catalog | undefined; priority: PicturePriority }) {
  return (
    <Link to={SHOWCASE_PATH[door.direction]} className={`${doorStyles.door} ${directionClass(door.direction)}`}>
      <DoorInside
        door={door}
        catalog={catalog}
        priority={priority}
        footerEnd={
          <span className={doorStyles.go}>
            <Icon name="arrow" size={16} strokeWidth={2.4} />
          </span>
        }
      />
    </Link>
  );
}

const STEPS = [
  { title: "Фото", text: "Селфи анфас, остальные по желанию", wide: "Сделай селфи. Для большего сходства добавь фото вполоборота и в профиль." },
  { title: "Образ", text: "Из каталога или своими словами", wide: "Раса и класс из каталога или любой образ своими словами." },
  { title: "Фигурка", text: "Эскиз за минуту, печать с доставкой", wide: "Эскиз примерно за минуту. Понравится — напечатаем и привезём." },
];

export function HomeScreen() {
  const { data: catalog } = useCatalog();
  // После «Выйти» в «Моих заказах»: главная для гостя и сообщение, что заказы на месте.
  // Сообщение — только после первой отрисовки: готовая главная (изменение seo) его не содержит,
  // а пометка выхода остаётся в истории и после обновления страницы.
  const loggedOutInHistory = (useLocation().state as { loggedOut?: boolean } | null)?.loggedOut === true;
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const loggedOut = mounted && loggedOutInHistory;
  return (
    <div className={styles.page}>
      <SiteHeader />
      <main className={styles.main}>
        {loggedOut && (
          <Message tone="ok">
            <b>Ты вышел на этом устройстве.</b> Заказы сохранены — войди снова, чтобы их увидеть.
          </Message>
        )}
        <div className={styles.top}>
          <section className={styles.hero}>
            <p className="eyebrow">из селфи — в фигурку</p>
            <h1 className={styles.title}>Фигурка с твоим лицом</h1>
            <p className={styles.lead}>
              Загрузи селфи и выбери образ — покажем эскиз фигурки. Понравится — напечатаем и привезём.
            </p>
            <div className={styles.desktopOnly}>
              <FreeAttemptNote />
            </div>
          </section>
          <section className={styles.doors} aria-label="Что хочешь сделать">
            {doorsFor(catalog).map((door, index) => (
              <DoorCard key={door.direction} door={door} catalog={catalog} priority={index === 0 ? "lead" : "eager"} />
            ))}
          </section>
        </div>
        <div className={`${styles.free} ${styles.mobileOnly}`}>
          <FreeAttemptNote />
        </div>

        <section className={styles.section} id="how">
          <h2 className="h2">Как это работает</h2>
          <ol className={styles.steps}>
            {STEPS.map((step, index) => (
              <li key={step.title} className={styles.step}>
                <span className={styles.stepNumber}>{index + 1}</span>
                <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span className={styles.stepTitle}>{step.title}</span>
                  <span className={`${styles.stepText} ${styles.mobileOnly}`}>{step.text}</span>
                  <span className={`${styles.stepText} ${styles.desktopOnly}`}>{step.wide}</span>
                </span>
              </li>
            ))}
          </ol>
          <Link to="/how" className={styles.allSteps}>
            Все шаги подробно — от селфи до посылки
            <Icon name="arrow" size={16} strokeWidth={2.4} />
          </Link>
        </section>

        <section className={`${styles.section} ${styles.mobileOnly}`}>
          <div className={styles.views}>
            <img {...picture("/showcase/views_front.jpg", SIZES_THIRD)} alt="Фигурка спереди" />
            <img {...picture("/showcase/views_side.jpg", SIZES_THIRD)} alt="Фигурка сбоку" />
            <img {...picture("/showcase/views_back.jpg", SIZES_THIRD)} alt="Фигурка сзади" />
          </div>
          <p className="text small muted">
            Так выглядит эскиз: спереди, сбоку и сзади. Пример работы, лицо сгенерировано.
          </p>
        </section>

        <SketchAndPrint catalog={catalog} />
        <section className={styles.section}>
          <Slogan title={MAIN_SLOGAN}>
            Эльф-следопыт, пират, киберсамурай — или ты в рыцарских доспехах. Придумай героя: лицо возьмём с селфи,
            остальное слепим и напечатаем.
          </Slogan>
        </section>
        <div className={styles.spacer} />
      </main>
      <SiteFooter />
    </div>
  );
}

// Цена печати строкой: «1 690 ₽» или для двух размеров «2 990 / 4 990 ₽». Нет цены — нет строки.
function printPrices(catalog: Catalog | undefined, direction: Direction, product: string, sizeIds: string[]): string | null {
  if (catalog === undefined) return null;
  const prices = sizeIds.map((sizeId) => priceOf(catalog, direction, product, sizeId));
  if (prices.some((price) => price === null)) return null;
  return `${prices.map((price) => new Intl.NumberFormat("ru-RU").format(price as number)).join(" / ")} ₽`;
}

// «Эскиз и печать» (макет «Компьютер · главная», версия 34, был «Одна фигурка — три вида»):
// эскиз бесплатно, цены — у печати, для миниатюры и для фигурки 10 и 15 см. Цветной печати
// нет: Иван, 24.10.2026, «на запуск только серая».
function SketchAndPrint({ catalog }: { catalog: Catalog | undefined }) {
  const prints = [
    {
      product: "print_grey",
      title: "Серая печать",
      text: "Серый пластик — можно покрасить самому.",
      image: "/showcase/example_grey.jpg",
      alt: "Та же фигурка серой, как выйдет из принтера",
    },
  ];
  return (
    <section className={`${styles.section} ${styles.examplesSection}`}>
      <div className={styles.examplesHeader}>
        <h2 className="h2">Эскиз и печать</h2>
        <span className="mono muted">пример работы, лицо сгенерировано</span>
      </div>
      <p className={styles.examplesLead}>
        Эскиз рисуем бесплатно. Перед оплатой покажем ту же фигурку серой — такой, какой она выйдет из принтера.
        Платишь только за печать: цена зависит от размера.
      </p>
      <div className={styles.examples}>
        <figure>
          <img {...picture("/showcase/example_concept.jpg", SIZES_HALF)} alt="Эскиз фигурки: спереди, сбоку, сзади" />
          <figcaption>
            <span className={styles.exampleTitle}>
              Эскиз <Tag tone="ok">бесплатно</Tag>
            </span>
            <span className="small muted">Рисуем по селфи примерно за минуту.</span>
          </figcaption>
        </figure>
        {prints.map((print) => {
          const mini = printPrices(catalog, "miniature", print.product, ["mini_40"]);
          const gift = printPrices(catalog, "gift", print.product, ["gift_10", "gift_15"]);
          return (
            <figure key={print.product}>
              <img {...picture(print.image, SIZES_HALF)} alt={print.alt} />
              <figcaption>
                <span className={styles.exampleTitle}>{print.title}</span>
                <span className="small muted">{print.text}</span>
                <span className={styles.examplePrices}>
                  {mini !== null && (
                    <span>
                      <span>Миниатюра, 40 мм</span>
                      <b>{mini}</b>
                    </span>
                  )}
                  {gift !== null && (
                    <span>
                      <span>Фигурка 10 / 15 см</span>
                      <b>{gift}</b>
                    </span>
                  )}
                </span>
              </figcaption>
            </figure>
          );
        })}
      </div>
    </section>
  );
}

function FreeAttemptNote() {
  return (
    <Message tone="info" icon="shield">
      <b>Эскиз — бесплатно.</b> Платишь, только если понравилось. Не понравится 3D-модель — переделаем
      или вернём деньги.
    </Message>
  );
}
